import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GeneralSetting } from "@apis/index";
import {
  PageContainer,
  Blob1,
  Blob2,
  Header,
  HeaderInner,
  Logo,
  NavLinks,
  NavLink,
  ContentWrapper,
  PageTitle,
  UpdatedDate,
  HtmlContent,
  ContactGrid,
  ContactCard,
  ContactLabel,
  ContactValue,
  LoaderContainer,
  FooterContainer,
  Copyright,
} from "./style";

export default function PageViewer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [contactInfo, setContactInfo] = useState<any | null>(null);

  const getPageConfig = (path: string) => {
    switch (path) {
      case "/privacy-policy":
        return {
          key: "customer-privacy-policy",
          title: "Privacy Policy",
          type: "policy",
        };
      case "/terms-of-service":
        return {
          key: "customer-terms-and-conditions",
          title: "Terms of Service",
          type: "policy",
        };
      case "/refund-policy":
        return {
          key: "customer-cancellation-policy",
          title: "Refund Policy",
          type: "policy",
        };
      case "/contact-us":
        return {
          key: "support-info",
          title: "Contact Us",
          type: "contact",
        };
      default:
        return null;
    }
  };

  useEffect(() => {
    const fetchPageContent = async () => {
      setLoading(true);
      setContentHtml("");
      setContactInfo(null);

      const config = getPageConfig(pathname);
      if (!config) {
        navigate("/referral");
        return;
      }

      setTitle(config.title);

      try {
        const res = await GeneralSetting.getByKey({
          id: config.key,
          showErr: false,
        });

        if (res) {
          if (config.type === "policy") {
            const cleanHtml = (res.value || "")
              .replace(/&nbsp;/g, " ")
              .replace(/\u00a0/g, " ")
              .replace(/\xa0/g, " ");
            setContentHtml(cleanHtml || "<p>No content available at the moment.</p>");
          } else if (config.type === "contact" && res.infos) {
            setContactInfo(res.infos);
          }
        } else {
          setContentHtml("<p>No content available at the moment.</p>");
        }
      } catch (error) {
        console.error("Failed to fetch page content:", error);
        setContentHtml("<p>Failed to load the page. Please try again later.</p>");
      } finally {
        setLoading(false);
      }
    };

    fetchPageContent();
  }, [pathname, navigate]);

  return (
    <PageContainer>
      <Blob1 />
      <Blob2 />

      {/* Navigation Header */}
      <Header>
        <HeaderInner>
          <Logo src="/emailfooterlogo.svg" alt="Zedfo" onClick={() => navigate("/referral")} />
          <NavLinks>
            <NavLink href="/admin-login">Log In</NavLink>
            <NavLink href="/referral">Referral Program</NavLink>
          </NavLinks>
        </HeaderInner>
      </Header>

      {/* Main Content Card */}
      <ContentWrapper>
        {loading ? (
          <LoaderContainer>Loading {title}...</LoaderContainer>
        ) : (
          <>
            <PageTitle>{title}</PageTitle>
            <UpdatedDate>Last Updated: June 2026</UpdatedDate>

            {/* Render Policy HTML content */}
            {contentHtml && (
              <HtmlContent dangerouslySetInnerHTML={{ __html: contentHtml }} />
            )}

            {/* Render Contact cards if page is Contact Us */}
            {contactInfo && (
              <ContactGrid>
                {contactInfo.name && (
                  <ContactCard>
                    <ContactLabel>Support Name</ContactLabel>
                    <ContactValue>{contactInfo.name}</ContactValue>
                  </ContactCard>
                )}
                {contactInfo.phoneNo && (
                  <ContactCard>
                    <ContactLabel>Phone Number</ContactLabel>
                    <ContactValue>{contactInfo.phoneNo}</ContactValue>
                  </ContactCard>
                )}
                {contactInfo.alternativePhoneNo && (
                  <ContactCard>
                    <ContactLabel>Alternative Phone</ContactLabel>
                    <ContactValue>{contactInfo.alternativePhoneNo}</ContactValue>
                  </ContactCard>
                )}
                {contactInfo.email && (
                  <ContactCard>
                    <ContactLabel>Email Address</ContactLabel>
                    <ContactValue>{contactInfo.email}</ContactValue>
                  </ContactCard>
                )}
                {contactInfo.address && (
                  <ContactCard>
                    <ContactLabel>Our Location</ContactLabel>
                    <ContactValue>{contactInfo.address}</ContactValue>
                  </ContactCard>
                )}
                {(contactInfo.whatsapp || contactInfo.whatapp) && (
                  <ContactCard>
                    <ContactLabel>WhatsApp Support</ContactLabel>
                    <ContactValue>
                      {contactInfo.whatsapp || contactInfo.whatapp}
                    </ContactValue>
                  </ContactCard>
                )}
              </ContactGrid>
            )}
          </>
        )}
      </ContentWrapper>

      {/* Simple Footer */}
      <FooterContainer>
        <Copyright>
          © 2026 Zedfo. All rights reserved. Registered trademark of Zedfo Nepal.
        </Copyright>
      </FooterContainer>
    </PageContainer>
  );
}
